import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@redux/hooks';

import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';

import { authSelectors, loginUser } from '@redux/auth';
import {
  SearchParams,
  requestPasswordlessSignInAPI,
  verifyPasswordlessSignInAPI,
} from '@constants';
import { useTitle } from '@hooks';
import { PageTitle } from 'constants/pageTitle';
import { logEvent } from '@services';
import { auth } from 'services/firebase';
import { signInWithEmailLink } from 'firebase/auth';
import { getPendingMagicToken, isEmail } from '@utils';

interface IPasswordlessVerificationResponse {
  email: string;
  emailLink: string;
}

export const SignInPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const setPageTitle = useTitle();
  const { data } = useAppSelector(authSelectors.getProfileAPIData);
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get(SearchParams.REDIRECT);
  const [magicToken] = useState(getPendingMagicToken);
  const magicExchangeStarted = useRef(false);

  const [email, setEmail] = useState('');
  const [isLinkSent, setIsLinkSent] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(magicToken));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPageTitle(PageTitle.SIGN_IN_PAGE);
  }, [setPageTitle]);

  useEffect(() => {
    if (data) navigate(redirectUrl || '/', { replace: true });
  }, [data, navigate, redirectUrl]);

  useEffect(() => {
    if (!magicToken || magicExchangeStarted.current) return;
    magicExchangeStarted.current = true;

    void (async () => {
      try {
        const { data: verification } =
          await axios.post<IPasswordlessVerificationResponse>(
            verifyPasswordlessSignInAPI,
            { magicToken },
          );

        await signInWithEmailLink(
          auth,
          verification.email,
          verification.emailLink,
        );
        logEvent('clients_loggedin', { 'login type': 'login/email-link' });
        await dispatch(loginUser()).unwrap();
      } catch {
        setError(
          'This login link is no longer valid. Enter your email below and we will send a new one.',
        );
        setIsLoading(false);
      }
    })();
  }, [dispatch, magicToken]);

  const requestLink = useCallback(async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!isEmail(normalizedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setError(null);
    setIsLoading(true);
    try {
      await axios.post(requestPasswordlessSignInAPI, {
        email: normalizedEmail,
      });
      setEmail(normalizedEmail);
      setIsLinkSent(true);
    } catch {
      setError('We could not send the email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  if (magicToken && isLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
        <CircularProgress />
        <Typography variant="body1">Logging you in…</Typography>
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h2" mb={{ xs: 2, md: 6 }}>
        Log in to your team dashboard
      </Typography>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        width="100%"
        maxWidth={{ xs: '100%', md: '320px' }}
        mb={4}
      >
        {!isLinkSent ? (
          <>
            <TextField
              autoFocus
              name="email"
              label="Email"
              variant="outlined"
              fullWidth
              value={email}
              type="email"
              error={Boolean(error)}
              onChange={(event) => {
                setEmail(event.target.value);
                setError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void requestLink();
              }}
              placeholder="name@company.com"
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              onClick={() => void requestLink()}
              disabled={!email.trim() || isLoading}
            >
              {isLoading && (
                <CircularProgress
                  size={16}
                  color="secondary"
                  sx={{ marginRight: 1 }}
                />
              )}
              Email me a login link
            </Button>
          </>
        ) : (
          <>
            <Typography variant="body1">
              If <strong>{email}</strong> belongs to a Rocky account, a login
              link is on its way. Open it on this device or any other — it works
              for 2 hours.
            </Typography>
            <Box display="flex" justifyContent="space-between" gap={2}>
              <Button
                variant="text"
                onClick={() => {
                  setIsLinkSent(false);
                  setError(null);
                }}
              >
                Change email
              </Button>
              <Button
                variant="text"
                onClick={() => void requestLink()}
                disabled={isLoading}
              >
                Resend link
              </Button>
            </Box>
          </>
        )}
        {error && (
          <Typography color="text.danger" variant="caption">
            {error}
          </Typography>
        )}
      </Box>
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant="body2" color="text.lightGrey">
          We’ll email you a secure login link. No password needed.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          If you have trouble accessing the app or want to add more people from
          your company, please contact your AgileEngine point of contact.
        </Typography>
      </Box>
    </>
  );
};
